import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types'
import { useAppDispatch } from '@store/index'
import { login } from '@store/slices/authSlice'
import AgencyHippaConsentDialog from './AgencyHippaConsentDialog'

interface AgencyHippaConsentGuardProps {
  children: React.ReactNode
}

const AgencyHippaConsentGuard: React.FC<AgencyHippaConsentGuardProps> = ({ children }) => {
  const { user, token, refreshToken, role } = useAuth()
  const dispatch = useAppDispatch()
  const [showConsentDialog, setShowConsentDialog] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkConsentStatus = async () => {
      if (role !== UserRole.AGENCY || !user) {
        setIsChecking(false)
        return
      }

      if (user.hasHippaSigned === false || user.hasHippaSigned === undefined) {
        setShowConsentDialog(true)
      }

      setIsChecking(false)
    }

    checkConsentStatus()
  }, [user, role])

  const handleConsentSubmitted = async () => {
    if (!user || !token) return

    const updatedUser = { ...user, hasHippaSigned: true }

    dispatch(
      login({
        user: updatedUser,
        token,
        refreshToken: refreshToken || '',
      })
    )

    setShowConsentDialog(false)
  }

  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying consent status...
        </Typography>
      </Box>
    )
  }

  if (showConsentDialog && user) {
    return (
      <>
        <AgencyHippaConsentDialog
          open={showConsentDialog}
          user={user}
          onConsentSubmitted={handleConsentSubmitted}
        />
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            pointerEvents: 'none',
          }}
        />
      </>
    )
  }

  return <>{children}</>
}

export default AgencyHippaConsentGuard
